import { Button, ButtonGroup } from "../button";
import { useTranslation } from "../localization";
import { Modal } from "../modal";

/** Props Interface */
export interface RecordingRecoveryModalProps {
	/** The page the recording was made for. */
	label: string;
	/** How much of it survived, already formatted. */
	duration: string;
	/** True when the destination is gone and nothing can be sent. */
	isLost: boolean;
	onSend: () => void;
	onDelete: () => void;
	onClose: () => void;
}

/**
 * The question waiting for an author whose browser died mid-recording.
 *
 * Built on `Modal` rather than `ConfirmModal` on purpose: that component wires its dismiss to its
 * reject, so the two are indistinguishable, and a stray Escape would then delete a tutorial. Here
 * each of the three ways out is its own control — send, delete, and leave it for later — and
 * closing the dialog is the one that changes nothing.
 */
export function RecordingRecoveryModal({
	label,
	duration,
	isLost,
	onSend,
	onDelete,
	onClose,
}: RecordingRecoveryModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Render */
	return (
		<Modal
			isOpen
			onClose={onClose}
			withWarning={false}
			width="30rem"
			title={t(isLost ? "video_recorder.recovery_lost_title" : "video_recorder.recovery_title")}
		>
			<Modal.Body>
				<p>
					{t(isLost ? "video_recorder.recovery_lost_message" : "video_recorder.recovery_message", {
						duration,
						label,
					})}
				</p>
			</Modal.Body>
			<Modal.Footer>
				<Button
					variant="danger-ghost"
					onClick={onDelete}
				>
					{t("video_recorder.recovery_delete")}
				</Button>
				<ButtonGroup isAttached={false}>
					<Button
						variant="ghost"
						onClick={onClose}
					>
						{t("general.close")}
					</Button>
					{!isLost && (
						<Button
							variant="primary"
							onClick={onSend}
						>
							{t("video_recorder.recovery_send")}
						</Button>
					)}
				</ButtonGroup>
			</Modal.Footer>
		</Modal>
	);
}
